import { useState } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { LogoLink } from '@/components/LogoLink';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonList,
  IonItem,
  IonLabel,
  IonNote,
  IonIcon,
  IonBadge,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/react';
import { briefcase, person, add, search, notifications } from 'ionicons/icons';
import { useAuthStore } from '@/stores/useAuthStore';
import { useMyEmployerProfile, useEmployerJobs } from '@/lib/api/hooks';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';

export function EmployerDashboard() {
  const role = useAuthStore((s) => s.roleSelection) ?? 'employer';
  const history = useHistory();
  const [view, setView] = useState<'employer' | 'candidate'>(role === 'candidate' ? 'candidate' : 'employer');

  const { data: employerProfile } = useMyEmployerProfile();
  const employerId = employerProfile?._id ?? '';
  const tenantId = employerProfile?.tenant?.id ?? '';
  const { data: jobsEnvelope } = useEmployerJobs(employerId, tenantId);

  if (role === 'candidate' || view === 'candidate') {
    return <Redirect to="/profile" />;
  }

  const activeJobs = jobsEnvelope?.data?.filter((j) => j.status === 'Open') ?? [];
  const totalMatches = activeJobs.length;

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <LogoLink style={{ height: '28px', marginTop: '4px' }} />
          </IonTitle>
        </IonToolbar>
        {role === 'both' && (
          <IonToolbar>
            <IonSegment value={view} onIonChange={(e) => setView(e.detail.value as 'employer' | 'candidate')}>
              <IonSegmentButton value="employer">
                <IonLabel><IonIcon icon={briefcase} /> Employer</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="candidate">
                <IonLabel><IonIcon icon={person} /> Candidate</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent className="ion-padding">
        <h2 className="bp-heading">Employer Dashboard</h2>

        <IonGrid>
          <IonRow>
            <IonCol size="6">
              <IonCard>
                <IonCardContent className="ion-text-center">
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--ion-color-primary)' }}>
                    {activeJobs.length}
                  </div>
                  <IonNote>Active jobs</IonNote>
                </IonCardContent>
              </IonCard>
            </IonCol>
            <IonCol size="6">
              <IonCard>
                <IonCardContent className="ion-text-center">
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--ion-color-primary)' }}>
                    {totalMatches}
                  </div>
                  <IonNote>Matched candidates</IonNote>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Quick actions</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol size="6">
                  <IonButton expand="block" fill="outline" onClick={() => history.push('/employer/jobs/new')}>
                    <IonIcon icon={add} slot="start" /> Post Job
                  </IonButton>
                </IonCol>
                <IonCol size="6">
                  <IonButton expand="block" fill="outline" onClick={() => history.push('/employer/profile')}>
                    <IonIcon icon={person} slot="start" /> Edit Profile
                  </IonButton>
                </IonCol>
                <IonCol size="6">
                  <IonButton expand="block" fill="outline" onClick={() => history.push('/employer/search')}>
                    <IonIcon icon={search} slot="start" /> Browse Candidates
                  </IonButton>
                </IonCol>
                <IonCol size="6">
                  <IonButton expand="block" fill="outline" onClick={() => history.push('/employer/jobs')}>
                    <IonIcon icon={briefcase} slot="start" /> Job Listings
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={notifications} /> Recent matches
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {activeJobs.length > 0 ? (
              <IonList lines="full">
                {activeJobs.slice(0, 3).map((j) => (
                  <IonItem key={j._id}>
                    <IonLabel>
                      <h3>{j.title}</h3>
                      <IonNote>{j.location}</IonNote>
                    </IonLabel>
                    <IonBadge slot="end" color="primary">{j.workType}</IonBadge>
                  </IonItem>
                ))}
              </IonList>
            ) : (
              <IonText color="medium">
                <p>No active job listings yet. Post your first job to start receiving candidate matches.</p>
              </IonText>
            )}
          </IonCardContent>
        </IonCard>
            <AppFooter />

      </IonContent>
    </IonPage>
  );
}
